import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/work", label: "Work" },
  { to: "/services", label: "Services" },
  { to: "/process", label: "Process" },
  { to: "/about", label: "About" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50">
        <div
          className={`mx-auto flex items-center justify-between transition-all duration-500 ease-out ${
            scrolled
              ? "mt-4 max-w-4xl rounded-full border border-white/10 bg-[#121418]/85 px-6 py-3 shadow-[0_8px_40px_rgba(0,0,0,0.45)] backdrop-blur-xl mx-4 sm:mx-auto"
              : "max-w-7xl border-b border-white/5 px-6 py-5 sm:px-8 lg:px-12"
          }`}
        >
          <Link
            to="/"
            data-testid="nav-wordmark-link"
            aria-label="Kronos home"
            className="flex items-center gap-3"
          >
            <img src="/kronos-mark-light.webp" alt="" className="h-7 w-auto" />
            <span className="font-display text-sm font-bold tracking-[0.4em] text-[#F4F4F6]">KRONOS</span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-links">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-underline text-[13px] tracking-wide transition-colors duration-300 ${
                    isActive ? "text-[#F4F4F6]" : "text-[#9498A4] hover:text-[#F4F4F6]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-start-project-button"
              className="hidden rounded-full bg-[#2563EB] px-5 py-2 text-[13px] font-medium text-white transition-colors duration-300 hover:bg-[#1D4ED8] sm:block"
            >
              Start a Project
            </Link>
            <button
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-[#F4F4F6] transition-colors hover:border-[#2563EB]/50 lg:hidden"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#0B0C0E]"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <span className="flex items-center gap-3">
                <img src="/kronos-mark-light.webp" alt="" className="h-7 w-auto" />
                <span className="font-display text-sm font-bold tracking-[0.4em]">KRONOS</span>
              </span>
              <button
                data-testid="mobile-menu-close-button"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-[#F4F4F6]"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className="block border-b border-white/5 py-4 font-display text-3xl font-semibold text-[#F4F4F6] transition-colors hover:text-[#2563EB]"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <Link
                to="/contact"
                data-testid="mobile-start-project-button"
                className="block rounded-full bg-[#2563EB] py-4 text-center font-medium text-white"
              >
                Start a Project
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
